import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import LoginForm from '../components/LoginForm';
import StaffDashboard from '../components/StaffDashboard';
import AdminDashboard from '../components/AdminDashboard';
import ManagerDashboard from '../components/ManagerDashboard';
import PasswordCreation from '../components/PasswordCreation';
import { MFAVerifyStep } from '../components/MFAVerifyStep';
import IdleTimeoutWarning from '../components/IdleTimeoutWarning';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { supabase } from '@/integrations/supabase/client';
import { ROLES } from '@/constants/roles';

import cimfoniLogo from '@/assets/cimfoni_logo_transparent.png';

const Index = () => {
  const { isAuthenticated, userRole, needsPasswordReset, setNeedsPasswordReset, mfaPending, completeMFA, logout, isLoading } = useAuth();
  const [userEmail, setUserEmail] = React.useState<string>('');

  const { showWarning, remainingSeconds, stayActive } = useIdleTimeout({
    timeoutMs: 30 * 60 * 1000, // 30 minutes
    onIdle: logout,
    enabled: isAuthenticated,
  });

  React.useEffect(() => {
    const getUserEmail = async () => {
      const { data: { user: supabaseUser } } = await supabase.auth.getUser();
      if (supabaseUser?.email) {
        setUserEmail(supabaseUser.email);
      }
    };
    
    if (isAuthenticated && needsPasswordReset) {
      getUserEmail();
    }
  }, [isAuthenticated, needsPasswordReset]);

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <img
          src={cimfoniLogo}
          alt="Loading"
          className="h-16 w-16 animate-spin opacity-80"
          style={{ animationDuration: '1.6s' }}
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  if (mfaPending) {
    return <MFAVerifyStep onVerified={completeMFA} onCancel={logout} />;
  }

  if (needsPasswordReset && userEmail) {
    return <PasswordCreation email={userEmail} onComplete={() => setNeedsPasswordReset(false)} />;
  }

  const dashboard = (() => {
    switch (userRole) {
      case ROLES.SUPER_ADMIN:
        return <AdminDashboard />;
      // Unified Manager UI for all manager-tier roles.
      // Per-role tab visibility is controlled by feature_toggles (managed
      // by Super Admin / Program Director from sidebar → Feature Toggles).
      case ROLES.PROGRAM_DIRECTOR:
      case ROLES.MANAGER:
      case ROLES.SUPERVISOR:
      case ROLES.CASE_MANAGER:
        return <ManagerDashboard />;
      case ROLES.RESIDENTIAL_AIDE:
      case ROLES.STAFF:
      default:
        return <StaffDashboard />;
    }
  })();

  return (
    <>
      {dashboard}
      <IdleTimeoutWarning
        open={showWarning}
        remainingSeconds={remainingSeconds}
        onStayActive={stayActive}
      />
    </>
  );
};

export default Index;
